<script>
// Smart Review rail (compare mode): renders page groups of before/after
// change crops, drawn client-side from the public page images (canvas
// drawImage with a source rect computed from the percentage bounding
// boxes). Persistent while comparing; collapsible to a slim edge strip.
// Scoping to a single file is driven by ReviewPage's pane file-following.

// One cached Image per page uri serves every crop on that page. A failed
// load is evicted from the cache — page rasters are multi-megabyte and a
// transient CDN hiccup must not permanently blank that page's crops.
const pageImageCache = {};

function loadPageImage(uri) {
  if (!pageImageCache[uri]) {
    pageImageCache[uri] = new Promise((resolve, reject) => {
      const img = new Image();
      // No crossOrigin: these canvases are display-only (never read back),
      // so taint is irrelevant — and a plain load shares the browser cache
      // entry with the panes' <img> requests instead of re-downloading
      // multi-megabyte rasters under a separate CORS cache key.
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = uri;
    });
    pageImageCache[uri].catch(() => {
      delete pageImageCache[uri];
    });
  }
  return pageImageCache[uri];
}

export default {
  name: "proof-change-timeline",
  props: {
    changeTracking: {
      type: Object,
      default: null,
    },
    // toProofVersionFileId to scope the timeline to a single file (badge click)
    scopedFileId: {
      type: Number,
      default: null,
    },
    // collapsed to the slim edge strip
    collapsed: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      expandedPages: {},
      expandedMinor: {},
    };
  },
  computed: {
    visibleJobs() {
      const ct = this.changeTracking;
      if (!ct || !Array.isArray(ct.jobs)) return [];
      if (this.scopedFileId) {
        return ct.jobs.filter(
          (j) => j.toProofVersionFileId === this.scopedFileId
        );
      }
      return ct.jobs;
    },
    totalNormalChanges() {
      return this.visibleJobs.reduce(
        (sum, j) => sum + (j.normalChangeCount || 0),
        0
      );
    },
    isAnalyzing() {
      const ct = this.changeTracking;
      if (!ct) return true;
      if (ct.status === "queued" || ct.status === "processing") return true;
      return this.visibleJobs.some(
        (j) => j.status === "queued" || j.status === "processing"
      );
    },
  },
  watch: {
    scopedFileId() {
      this.autoExpandDefaultPages();
      // scoping re-renders the job sections, which recreates the crop
      // canvases blank — the pixels only exist in the old elements. Redraw
      // every expanded page, not just newly-expanded ones.
      this.redrawExpandedCrops();
    },
    changeTracking() {
      // data refreshed (initial load or poll) — pages that just appeared
      // get the rail's expanded-by-default treatment too
      this.autoExpandDefaultPages();
      this.redrawExpandedCrops();
    },
  },
  created() {
    // non-reactive: pending crop-retry timers, cleared on destroy
    this._cropRetryTimers = [];
  },
  mounted() {
    this.autoExpandDefaultPages();
  },
  beforeDestroy() {
    this._cropRetryTimers.forEach(clearTimeout);
    this._cropRetryTimers = [];
  },
  methods: {
    jobKey(job) {
      return String(job.toProofVersionFileId || job.fileName);
    },
    pageKey(job, pageIndex) {
      return this.jobKey(job) + ":" + pageIndex;
    },
    jobState(job) {
      if (job.status === "queued" || job.status === "processing")
        return "analyzing";
      if (job.status === "failed") return "failed";
      if (job.status === "skipped") {
        return job.skipReason === "unchanged" ? "unchanged" : "skipped";
      }
      if (job.status === "complete") {
        const hasPages = Array.isArray(job.pages) && job.pages.length > 0;
        if (!hasPages && !job.normalChangeCount && !job.minorChangeCount)
          return "unchanged";
        return "changes";
      }
      return "analyzing";
    },
    skipReasonText(job) {
      if (job.skipReason === "unsupportedType") {
        return "Change tracking isn't supported for this file type.";
      }
      if (job.skipReason === "ambiguous") {
        return "We couldn't confidently match this file to one from the previous version.";
      }
      if (job.skipReason === "missingRasters") {
        return "Preview images aren't available to compare for this file.";
      }
      if (job.skipReason === "tooManyPages") {
        return "This file is too large to compare (over 50 pages).";
      }
      return "Comparison was skipped for this file.";
    },
    normalChangesOf(page) {
      return (page.changes || []).filter((c) => c.severity !== "minor");
    },
    minorChangesOf(page) {
      return (page.changes || []).filter((c) => c.severity === "minor");
    },
    pageTitle(page) {
      if (page.pageChangeType === "added") {
        return "Page " + page.toPageNumber + " · page added";
      }
      if (page.pageChangeType === "removed") {
        return "Page " + page.fromPageNumber + " · page removed";
      }
      return "Page " + (page.toPageNumber || page.fromPageNumber);
    },
    isPageExpanded(job, pageIndex) {
      return !!this.expandedPages[this.pageKey(job, pageIndex)];
    },
    isMinorExpanded(job, pageIndex) {
      return !!this.expandedMinor[this.pageKey(job, pageIndex)];
    },
    togglePage(job, page, pageIndex) {
      const key = this.pageKey(job, pageIndex);
      const expanded = !this.expandedPages[key];
      this.$set(this.expandedPages, key, expanded);
      if (expanded) {
        this.$nextTick(() => this.drawPageCrops(job, page, pageIndex));
      }
    },
    toggleMinor(job, page, pageIndex) {
      const key = this.pageKey(job, pageIndex);
      const expanded = !this.expandedMinor[key];
      this.$set(this.expandedMinor, key, expanded);
      if (expanded) {
        this.$nextTick(() => this.drawPageCrops(job, page, pageIndex, true));
      }
    },
    selectChange(change, page, job) {
      this.$emit("change-selected", { change: change, page: page, job: job });
    },
    autoExpandDefaultPages() {
      // rail: every page row renders expanded by default so change cards are
      // visible without clicking. Only pages the user has never toggled
      // (key absent from expandedPages) are auto-expanded, so an explicit
      // collapse survives poll refreshes.
      const toDraw = [];
      this.visibleJobs.forEach((job) => {
        (job.pages || []).forEach((page, pageIndex) => {
          const key = this.pageKey(job, pageIndex);
          if (this.expandedPages[key] === undefined) {
            this.$set(this.expandedPages, key, true);
            toDraw.push({ job: job, page: page, pageIndex: pageIndex });
          }
        });
      });
      if (toDraw.length === 0) return;
      this.$nextTick(() => {
        toDraw.forEach((item) =>
          this.drawPageCrops(item.job, item.page, item.pageIndex)
        );
      });
    },
    redrawExpandedCrops() {
      // data refreshed (poll finished) — redraw crops for any expanded pages
      this.$nextTick(() => {
        this.visibleJobs.forEach((job) => {
          (job.pages || []).forEach((page, pageIndex) => {
            if (this.isPageExpanded(job, pageIndex)) {
              this.drawPageCrops(
                job,
                page,
                pageIndex,
                this.isMinorExpanded(job, pageIndex)
              );
            }
          });
        });
      });
    },
    drawPageCrops(job, page, pageIndex, includeMinor) {
      const draw = (changes, group) => {
        changes.forEach((change, changeIndex) => {
          const base =
            "crop_" +
            group +
            "_" +
            this.pageKey(job, pageIndex) +
            "_" +
            changeIndex;
          if (page.beforePageUri && page.pageChangeType !== "added") {
            this.renderCrop(base + "_before", page.beforePageUri, change, false);
          }
          if (page.afterPageUri && page.pageChangeType !== "removed") {
            this.renderCrop(base + "_after", page.afterPageUri, change, true);
          }
        });
      };
      draw(this.normalChangesOf(page), "n");
      if (includeMinor || this.isMinorExpanded(job, pageIndex)) {
        draw(this.minorChangesOf(page), "m");
      }
    },
    async renderCrop(refName, uri, change, outlineRegion, isRetry) {
      if (!uri || !this.$refs[refName]) return;
      let img;
      try {
        img = await loadPageImage(uri);
      } catch (e) {
        // one automatic retry after a beat — self-heals transient CDN blips;
        // a second failure leaves the placeholder background
        if (!isRetry) {
          this._cropRetryTimers.push(
            setTimeout(() => {
              this.renderCrop(refName, uri, change, outlineRegion, true);
            }, 1500)
          );
        }
        return;
      }
      // resolve the ref AFTER the await: a re-render while the image loaded
      // replaces the canvas element, and drawing into the old one is invisible
      const refs = this.$refs[refName];
      const canvasEl = Array.isArray(refs) ? refs[0] : refs;
      if (!canvasEl) return;
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      if (!w || !h) return;
      // pad the crop box ~15% of the region (min 1% of the page) for context
      const padX = Math.max((change.widthPct || 0) * 0.15, 0.01);
      const padY = Math.max((change.heightPct || 0) * 0.15, 0.01);
      const sx = Math.max(0, (change.xPct - padX) * w);
      const sy = Math.max(0, (change.yPct - padY) * h);
      const sw = Math.min(w - sx, (change.widthPct + padX * 2) * w);
      const sh = Math.min(h - sy, (change.heightPct + padY * 2) * h);
      if (sw <= 0 || sh <= 0) return;
      const maxCanvasWidth = 600;
      const scale = Math.min(1, maxCanvasWidth / sw);
      canvasEl.width = Math.max(1, Math.round(sw * scale));
      canvasEl.height = Math.max(1, Math.round(sh * scale));
      const ctx = canvasEl.getContext("2d");
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvasEl.width, canvasEl.height);
      if (outlineRegion) {
        ctx.strokeStyle = "rgba(212, 77, 104, 0.9)";
        ctx.lineWidth = 2;
        ctx.strokeRect(
          (change.xPct * w - sx) * scale,
          (change.yPct * h - sy) * scale,
          change.widthPct * w * scale,
          change.heightPct * h * scale
        );
      }
    },
  },
};
</script>

<template>
  <div
    class="change-timeline d-flex flex-column bg-ashore-white h-100"
  >
    <!-- collapsed: a slim strip that re-expands on click. The expanded DOM
         stays mounted (v-show) so the crop canvases keep their pixels. -->
    <div
      v-if="collapsed"
      class="rail-collapsed-strip"
      role="button"
      title="Expand Smart Review"
      @click="$emit('toggle-collapsed')"
    >
      <span class="rail-toggle-icon" aria-hidden="true">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </span>
      <b-badge v-if="totalNormalChanges > 0" variant="dark">
        {{ totalNormalChanges }}
      </b-badge>
      <span class="rail-collapsed-label">Smart Review</span>
    </div>
    <!-- header (rail only — the sidebar accordion supplies its own).
         The rail is persistent in compare mode: no close, only collapse.
         The panes already name the file, so the header doesn't repeat it. -->
    <div
      v-show="!collapsed"
      class="px-3 py-2 border-bottom flex-shrink-0 d-flex align-items-center"
    >
      <strong class="fs-14">Smart Review</strong>
      <span class="beta-pill ml-2 mr-auto">Beta</span>
      <b-spinner
        v-if="isAnalyzing"
        small
        class="ml-2 text-gray-500"
        label="Analyzing"
      />
      <b-badge v-else-if="totalNormalChanges > 0" variant="dark" class="ml-2">
        {{ totalNormalChanges }}
      </b-badge>
      <button
        class="rail-toggle-btn ml-2"
        type="button"
        title="Collapse Smart Review"
        aria-label="Collapse Smart Review"
        @click="$emit('toggle-collapsed')"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>
    </div>
    <!-- body -->
    <div
      v-show="!collapsed"
      class="flex-fill overflow-auto timeline-scroll">
      <!-- nothing loaded yet -->
      <div
        v-if="!changeTracking"
        class="d-flex align-items-center justify-content-center py-5 text-gray-600 fs-13"
      >
        <b-spinner small class="mr-2" />
        Analyzing changes…
      </div>
      <template v-else>
        <div
          v-if="visibleJobs.length === 0"
          class="px-3 py-4 text-gray-600 fs-13"
        >
          <template v-if="isAnalyzing">
            <b-spinner small class="mr-2" />
            Analyzing changes…
          </template>
          <template v-else>
            No comparison available for this file.
          </template>
        </div>
        <!-- per-file sections -->
        <section
          v-for="job in visibleJobs"
          :key="jobKey(job)"
          class="job-section"
        >
          <div
            v-if="!scopedFileId"
            class="d-flex align-items-center px-3 pt-3 pb-2"
          >
            <span class="font-weight-bold fs-13 text-truncate mr-auto">
              {{ job.fileName }}
            </span>
            <b-badge
              v-if="jobState(job) === 'changes' && job.minorChangeCount > 0"
              variant="light"
              class="ml-1 flex-shrink-0 text-gray-600"
            >
              +{{ job.minorChangeCount }} minor
            </b-badge>
          </div>

          <template>
            <!-- analyzing -->
            <div
              v-if="jobState(job) === 'analyzing'"
              class="px-3 pb-3 text-gray-600 fs-13"
            >
              <b-spinner small class="mr-2" />
              Analyzing…
            </div>

            <!-- failed -->
            <div
              v-else-if="jobState(job) === 'failed'"
              class="px-3 pb-3 text-danger fs-13"
            >
              Change detection failed for this file.
            </div>

            <!-- skipped -->
            <div
              v-else-if="jobState(job) === 'skipped'"
              class="px-3 pb-3 text-gray-600 fs-13"
            >
              {{ skipReasonText(job) }}
            </div>

            <!-- verified unchanged: a positive result, not an empty one.
                 Scoped (file fills the rail) gets the full state block;
                 the unscoped multi-file list keeps a compact row. -->
            <div
              v-else-if="jobState(job) === 'unchanged' && scopedFileId"
              class="verified-unchanged"
            >
              <span class="verified-unchanged-icon" aria-hidden="true">
                <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                  <rect x="5" y="5" width="21" height="21" rx="4" stroke="currentColor" stroke-width="2.5"/>
                  <rect x="14" y="14" width="21" height="21" rx="4" stroke="currentColor" stroke-width="2.5" fill="#F9F9F9"/>
                  <path d="M19.5 24.5l3.5 3.5 6.5-6.5" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
              <span class="verified-unchanged-title">No changes in this version</span>
              <span class="verified-unchanged-copy">
                We compared every page of this file with the previous version — they match.
              </span>
            </div>
            <div
              v-else-if="jobState(job) === 'unchanged'"
              class="px-3 pb-3 text-gray-600 fs-13"
            >
              No changes in this version.
            </div>

            <!-- changes: collapsed page rows -->
            <div v-else class="pb-2">
              <div
                v-for="(page, pageIndex) in job.pages"
                :key="pageKey(job, pageIndex)"
                class="page-group"
              >
                <div
                  class="page-row d-flex align-items-center px-3 py-2"
                  role="button"
                  @click.stop="togglePage(job, page, pageIndex)"
                >
                  <span class="mr-2 fs-12 text-gray-500">
                    {{ isPageExpanded(job, pageIndex) ? "▾" : "▸" }}
                  </span>
                  <span class="fs-13 mr-auto">{{ pageTitle(page) }}</span>
                </div>
                <div v-if="isPageExpanded(job, pageIndex)" class="px-3 pb-2">
                  <!-- added/removed whole pages -->
                  <div
                    v-if="page.pageChangeType"
                    class="change-card p-2 mb-2"
                    role="button"
                    @click="selectChange(null, page, job)"
                  >
                    <span class="fs-12 text-gray-700">
                      This page was
                      {{ page.pageChangeType === "added" ? "added" : "removed" }}
                      in this version. Click to view.
                    </span>
                  </div>
                  <!-- normal change cards -->
                  <div
                    v-for="(change, changeIndex) in normalChangesOf(page)"
                    :key="'n_' + changeIndex"
                    class="change-card p-2 mb-2"
                    role="button"
                    @click="selectChange(change, page, job)"
                  >
                    <div class="d-flex crop-pair mb-1">
                      <div
                        v-if="page.beforePageUri && page.pageChangeType !== 'added'"
                        class="crop-wrap mr-1"
                      >
                        <canvas
                          :ref="
                            'crop_n_' +
                            pageKey(job, pageIndex) +
                            '_' +
                            changeIndex +
                            '_before'
                          "
                          class="crop-canvas"
                        ></canvas>
                        <span class="crop-label">Before</span>
                      </div>
                      <div
                        v-if="page.afterPageUri && page.pageChangeType !== 'removed'"
                        class="crop-wrap"
                      >
                        <canvas
                          :ref="
                            'crop_n_' +
                            pageKey(job, pageIndex) +
                            '_' +
                            changeIndex +
                            '_after'
                          "
                          class="crop-canvas"
                        ></canvas>
                        <span class="crop-label">After</span>
                      </div>
                    </div>
                  </div>
                  <!-- minor changes collapse into one row per page -->
                  <div v-if="minorChangesOf(page).length > 0">
                    <div class="d-flex align-items-center py-1">
                      <span
                        class="fs-12 text-gray-500 mr-auto"
                        role="button"
                        @click.stop="toggleMinor(job, page, pageIndex)"
                      >
                        {{ isMinorExpanded(job, pageIndex) ? "▾" : "▸" }}
                        minor rendering differences ({{
                          minorChangesOf(page).length
                        }})
                      </span>
                    </div>
                    <template v-if="isMinorExpanded(job, pageIndex)">
                      <div
                        v-for="(change, changeIndex) in minorChangesOf(page)"
                        :key="'m_' + changeIndex"
                        class="change-card change-card-minor p-2 mb-2"
                        role="button"
                        @click="selectChange(change, page, job)"
                      >
                        <div class="d-flex crop-pair mb-1">
                          <div
                            v-if="
                              page.beforePageUri && page.pageChangeType !== 'added'
                            "
                            class="crop-wrap mr-1"
                          >
                            <canvas
                              :ref="
                                'crop_m_' +
                                pageKey(job, pageIndex) +
                                '_' +
                                changeIndex +
                                '_before'
                              "
                              class="crop-canvas"
                            ></canvas>
                            <span class="crop-label">Before</span>
                          </div>
                          <div
                            v-if="
                              page.afterPageUri &&
                              page.pageChangeType !== 'removed'
                            "
                            class="crop-wrap"
                          >
                            <canvas
                              :ref="
                                'crop_m_' +
                                pageKey(job, pageIndex) +
                                '_' +
                                changeIndex +
                                '_after'
                              "
                              class="crop-canvas"
                            ></canvas>
                            <span class="crop-label">After</span>
                          </div>
                        </div>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </section>
      </template>
    </div>
  </div>
</template>

<style scoped>
/* rail collapse: header toggle + the collapsed edge strip */
.rail-toggle-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: #8a8a92;
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease;
}
.rail-toggle-btn:hover {
  background: rgba(15, 23, 42, 0.06);
  color: #252526;
}
.rail-collapsed-strip {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  height: 100%;
  padding: 12px 0;
  cursor: pointer;
}
.rail-collapsed-strip:hover {
  background: rgba(15, 23, 42, 0.03);
}
.rail-toggle-icon {
  display: inline-flex;
  color: #8a8a92;
}
.rail-collapsed-label {
  writing-mode: vertical-rl;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: #6b6b76;
  user-select: none;
}
/* Smart Review beta marker — the feature's purple, quiet and small */
.beta-pill {
  display: inline-flex;
  align-items: center;
  padding: 1px 7px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  background-color: rgba(109, 91, 208, 0.12);
  color: #6d5bd0;
}
/* verified-unchanged: centered positive state in the upper third of the rail */
.verified-unchanged {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 56px 28px 24px;
}
.verified-unchanged-icon {
  display: inline-flex;
  color: #1d7a44;
  margin-bottom: 14px;
}
.verified-unchanged-title {
  font-size: 14px;
  font-weight: 600;
  color: #252526;
  margin-bottom: 4px;
}
.verified-unchanged-copy {
  font-size: 12.5px;
  line-height: 1.5;
  color: #6b6b76;
  max-width: 240px;
}
/* separators between file sections only — nothing after the last one */
.job-section + .job-section {
  border-top: 1px solid #ebebed;
}
/* the rail's list scrolls on its own; wheel momentum never chains
   out to the page scroll */
.timeline-scroll {
  min-height: 0;
  overscroll-behavior: contain;
}
.change-timeline {
  font-size: 13px;
}
.fs-11 {
  font-size: 11px;
}
.fs-12 {
  font-size: 12px;
}
.fs-13 {
  font-size: 13px;
}
.page-row {
  cursor: pointer;
  user-select: none;
}
.page-row:hover {
  background-color: #f4f4f6;
}
.change-card {
  border: 1px solid #ebebed;
  border-radius: 6px;
  cursor: pointer;
  background-color: #fff;
}
.change-card:hover {
  border-color: #448be2;
}
.change-card-minor {
  opacity: 0.85;
}
.crop-pair {
  gap: 4px;
}
.crop-wrap {
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  background-color: #f4f4f6;
  border-radius: 4px;
  overflow: hidden;
}
.crop-canvas {
  /* uniform crop window: every card is the same height; the region is
     drawn contained and letterboxed on the neutral wrap background */
  display: block;
  width: 100%;
  height: 96px;
  object-fit: contain;
}
.crop-label {
  position: absolute;
  left: 2px;
  top: 2px;
  font-size: 10px;
  line-height: 1;
  padding: 2px 4px;
  border-radius: 3px;
  background-color: rgba(0, 0, 0, 0.55);
  color: #fff;
  pointer-events: none;
}
</style>
